// Assembly Orders (Configuration › Assembly): every confirmed order, for Assembly to review and accept. Sales
// confirms an order in the configurator; it lands here to accept, and so does every new revision of it (with
// what changed since the revision Assembly last accepted). Assembly then puts it in production when the boat
// starts being built from it. A confirmed order is locked for Sales: Assembly opens it for them to change (one
// revision, a reason, a time limit), or changes it in the configurator. The order's creator is told each step.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { awaitsAcceptance, formatHelmShort, orderStage, resolveHelm } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { IdentityEditModal } from '../components/IdentityEditModal.js';
import { ChangeList, OpenForSalesModal, StagePill, parseChanges } from '../components/OrderWorkflow.js';
import { EmptyState, ErrorState, Fact, FactEnd, FactSentence, InlineError, LoadingState, Modal, PageHeader, SearchInput } from '../components/ui.js';
import { IconCheck, IconDownload, IconLock, IconUnlock } from '../components/icons.js';
import {
  useAcceptOrder,
  useExportLog,
  useLockOrder,
  useOrder,
  useProductionOrders,
  useRelockOrder,
  useRequirementAction,
  type ProdOrderSummary,
  type SpecField,
} from '../features/orders.js';
import { useNameResolver } from '../features/directory.js';
import { exportAssemblyList, groupByCategory } from '../features/exportAssemblyList.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

type Filter = 'accept' | 'accepted' | 'production' | 'open';
const FILTERS: Record<Filter, (o: ProdOrderSummary) => boolean> = {
  accept: (o) => awaitsAcceptance(o),
  accepted: (o) => orderStage(o) === 'accepted',
  production: (o) => !!o.inProduction,
  open: (o) => !!o.salesOpen,
};
const money = (n: number | null | undefined) => `€${(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** one order to review: where it stands, what changed since Assembly last accepted it, the order itself and its
 *  requirements; and Assembly's actions on it */
function ReviewModal({ id, specFields, categoryOrder, onClose }: { id: number; specFields: SpecField[]; categoryOrder: string[]; onClose: () => void }) {
  const { data, isLoading, error } = useOrder(id);
  const { can } = useAuth();
  const navigate = useNavigate();
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const accept = useAcceptOrder();
  const lock = useLockOrder();
  const relock = useRelockOrder();
  const req = useRequirementAction();
  const exportLog = useExportLog();
  const [opening, setOpening] = useState(false);
  const [details, setDetails] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<unknown>(null);

  const title = data ? (data.snapshot.vessel?.vesselName || data.snapshot.buyer?.name || data.order.orderNumber) : 'Order';
  const doExport = async () => {
    if (!data) return;
    setExporting(true);
    setExportErr(null);
    try {
      const filename = await exportAssemblyList(data, specFields, categoryOrder, df.prefs);
      const rev = data.revisions[data.revisions.length - 1];
      exportLog.mutate({ id: data.order.id, revisionId: rev?.id ?? data.order.id, filename });
    } catch (e) {
      setExportErr(e);
    } finally {
      setExporting(false);
    }
  };

  const failure = accept.error ?? lock.error ?? relock.error ?? req.error ?? exportErr ?? exportLog.error;
  const resetAll = () => { accept.reset(); lock.reset(); relock.reset(); req.reset(); exportLog.reset(); setExportErr(null); };

  let body = null;
  if (data) {
    const o = data.order;
    const s = data.snapshot;
    const toAccept = awaitsAcceptance(o);
    const committed = data.revisions.filter((r) => r.revision_type === 'committed').sort((a, b) => a.revision_number - b.revision_number);
    const since = o.acceptedRevision ?? null;
    const newer = since == null ? [] : committed.filter((r) => r.revision_number > since);
    const first = committed[0];
    const sel = s.equipment.filter((e) => e.selected && !e.isStandard);
    const off = s.equipment.filter((e) => e.removedFromStandard);
    const requests = (s.customItems ?? []).filter((c) => c.title?.trim());
    const manager = can('orderlock:manage');
    const named = (e: (typeof sel)[number]) => {
      const hs = resolveHelm(e);
      return `${hs ? formatHelmShort(hs) : (e.name ?? `#${e.equipmentId}`)}${e.quantity && e.quantity > 1 ? ` × ${e.quantity}` : ''}`;
    };

    body = (
      <>
        <div className="ao-state">
          <StagePill order={o} side="assembly" />
          <p>
            {toAccept && since == null && <>Confirmed by {nameOf(first?.created_by ?? o.createdBy)}{first ? ` on ${df.dateTime(first.created_at)}` : ''}. Review the order, then accept it.</>}
            {toAccept && since != null && <>Revision {o.revisionNumber} came in since you accepted revision {since}: what changed is below.</>}
            {!toAccept && since != null && <>Revision {since} accepted by {nameOf(o.acceptedBy)}{o.acceptedAt ? ` on ${df.dateTime(o.acceptedAt)}` : ''}.</>}
            {o.inProduction && <> In production since {o.productionLockedAt ? df.dateTime(o.productionLockedAt) : 'earlier'}{o.productionLockedBy ? ` (${nameOf(o.productionLockedBy)})` : ''}.</>}
          </p>
        </div>
        {o.salesOpen && (
          <div className="cfg-lock is-open" role="status">
            <IconUnlock size={16} />
            <div>
              <b>Open for Sales{o.salesOpen.until ? ` until ${df.dateTime(o.salesOpen.until)}` : ''}.</b> {o.salesOpen.reason}
              {o.salesOpen.instructions ? `: ${o.salesOpen.instructions}` : ''}{o.salesOpen.by ? ` (${nameOf(o.salesOpen.by)})` : ''}
            </div>
          </div>
        )}

        <div className="ao-acts-bar">
          {manager && toAccept && (
            <button className="btn btn-primary" disabled={accept.isPending} onClick={() => accept.mutate({ id: o.id, revision: o.revisionNumber })}>
              <IconCheck size={15} /> {accept.isPending ? 'Accepting…' : `Accept revision ${o.revisionNumber}`}
            </button>
          )}
          {manager && (
            <button
              className="btn"
              disabled={lock.isPending || (!o.inProduction && toAccept)}
              title={!o.inProduction && toAccept ? 'Accept its latest revision first' : undefined}
              onClick={() => lock.mutate({ id: o.id, locked: !o.inProduction })}
            >
              <IconLock size={15} /> {o.inProduction ? 'Take out of production' : 'Put in production'}
            </button>
          )}
          {can('salesorder:unlock') && !o.inProduction && !o.salesOpen && (
            <button className="btn" onClick={() => setOpening(true)}><IconUnlock size={15} /> Open for Sales</button>
          )}
          {can('salesorder:relock') && o.salesOpen && (
            <button className="btn" disabled={relock.isPending} onClick={() => relock.mutate({ id: o.id, reason: 'Locked again by Assembly' })}><IconLock size={15} /> Lock again</button>
          )}
          {manager && <button className="btn btn-ghost" onClick={() => navigate(`/configurator?id=${o.id}`)}>Change in the configurator</button>}
          {can('productionlist:export') && (
            <button className="btn btn-ghost" disabled={exporting} onClick={() => void doExport()}><IconDownload size={15} /> {exporting ? 'Exporting…' : 'Assembly list'}</button>
          )}
          {(can('order:create') || can('prodnotes:write')) && <button className="btn btn-ghost" onClick={() => setDetails(true)}>Customer / vessel / SAP</button>}
        </div>
        {failure != null && <InlineError error={failure} onDismiss={resetAll} />}

        {newer.length > 0 && (
          <section className="ao-sec">
            <h3 className="ao-sec-t">What changed since revision {since}</h3>
            {newer.map((r) => {
              const changes = parseChanges(r.change_summary);
              return (
                <div key={r.id} className="ao-rev">
                  <div className="ao-rev-hd">Revision {r.revision_number} · {nameOf(r.created_by)} · {df.dateTime(r.created_at)}{r.confirmation_note ? ` · “${r.confirmation_note}”` : ''}</div>
                  {changes.length ? <ChangeList changes={changes} /> : <p className="muted ao-none">Saved with no change to the configuration.</p>}
                </div>
              );
            })}
          </section>
        )}

        <section className="ao-sec">
          <h3 className="ao-sec-t">The order</h3>
          <dl className="ao-facts">
            <dt>Order</dt><dd>{o.orderNumber}{o.proformaNumber ? ` · proforma ${o.proformaNumber}` : ''}</dd>
            <dt>Boat</dt><dd>{[s.modelName, s.vessel?.vesselName].filter(Boolean).join(' · ') || '-'}</dd>
            <dt>SAP</dt><dd>{o.sapNumber || <span className="muted">Not given yet</span>}</dd>
            <dt>Customer</dt><dd>{s.buyer?.name || '-'}</dd>
            {s.dealer && <><dt>Dealer</dt><dd>{s.dealer}</dd></>}
            {s.delivery?.estimatedDeliveryPeriod && <><dt>Delivery</dt><dd>{s.delivery.estimatedDeliveryPeriod}</dd></>}
            <dt>Total</dt><dd>{money(s.pricing?.total)} incl. VAT</dd>
            <dt>Sales</dt><dd>{nameOf(o.createdBy)}</dd>
          </dl>
          <div className="ao-lists">
            <div>
              <h4 className="ao-list-t">Options ({sel.length})</h4>
              {sel.length ? <ul className="so-prev-list">{groupByCategory(sel, categoryOrder).map((e, i) => <li key={i}>{named(e)}{e.color ? <span className="ao-color"> · {e.color}</span> : null}</li>)}</ul> : <p className="muted ao-none">None.</p>}
            </div>
            {off.length > 0 && (
              <div>
                <h4 className="ao-list-t">Standard taken off ({off.length})</h4>
                <ul className="so-prev-list is-removed">{groupByCategory(off, categoryOrder).map((e, i) => <li key={i}>{named(e)}</li>)}</ul>
              </div>
            )}
            {requests.length > 0 && (
              <div>
                <h4 className="ao-list-t">Special requests ({requests.length})</h4>
                <ul className="so-prev-list">{requests.map((c, i) => <li key={i}>{c.title}{c.description ? `: ${c.description}` : ''}{c.quantity && c.quantity > 1 ? ` × ${c.quantity}` : ''}</li>)}</ul>
              </div>
            )}
          </div>
        </section>

        <section className="ao-sec">
          <h3 className="ao-sec-t">Requirements ({data.requirements.length})</h3>
          {data.requirements.length === 0 && <p className="muted ao-none">None.</p>}
          {data.requirements.map((r) => (
            <div key={r.id} className={`ao-req${r.completed_at ? ' is-done' : ''}`}>
              <span className="ao-req-t">
                {r.source === 'custom' && <span className="pill warn">request</span>} {r.description}
                {r.acknowledged_at && !r.completed_at && <span className="muted"> · seen by {nameOf(r.acknowledged_by)}</span>}
                {r.completed_at && <span className="muted"> · done by {nameOf(r.completed_by)}</span>}
              </span>
              {can('prodnotes:write') && (
                <span className="ao-req-acts">
                  <button className="btn btn-sm btn-ghost" disabled={req.isPending} aria-pressed={!!r.acknowledged_at} onClick={() => req.mutate({ id: r.id, action: r.acknowledged_at ? 'unack' : 'ack', orderId: o.id })}>Seen</button>
                  <button className="btn btn-sm" disabled={req.isPending} aria-pressed={!!r.completed_at} onClick={() => req.mutate({ id: r.id, action: r.completed_at ? 'incomplete' : 'complete', orderId: o.id })}>{r.completed_at ? 'Not done' : 'Done'}</button>
                </span>
              )}
            </div>
          ))}
        </section>

        {committed.length > 1 && (
          <details className="ao-sec ao-hist">
            <summary className="ao-sec-t">Every revision ({committed.length})</summary>
            {[...committed].reverse().map((r) => (
              <div key={r.id} className="ao-rev">
                <div className="ao-rev-hd">
                  Revision {r.revision_number} · {nameOf(r.created_by)} · {df.dateTime(r.created_at)}
                  {since != null && r.revision_number === since ? ' · accepted' : ''}
                </div>
                <ChangeList changes={parseChanges(r.change_summary)} />
              </div>
            ))}
          </details>
        )}
      </>
    );
  }

  return (
    <>
      <Modal title={`${title} · review`} onClose={onClose} width={720} footer={<button className="btn btn-ghost" onClick={onClose}>Close</button>}>
        {isLoading && <LoadingState />}
        {error && <ErrorState error={error} />}
        {body}
      </Modal>
      {opening && data && <OpenForSalesModal order={data.order} onClose={() => setOpening(false)} />}
      {details && <IdentityEditModal id={id} onClose={() => setDetails(false)} />}
    </>
  );
}

export function AssemblyOrders() {
  const { data, isLoading, isFetching, error, refetch } = useProductionOrders();
  const { can } = useAuth();
  const fmtDate = useFormatDate();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const [params] = useSearchParams(); // ?q= (an email's button, Sales Orders' "Assembly →") lands on the order
  const [q, setQ] = useState(params.get('q') ?? '');
  const [filter, setFilter] = useState<Filter | null>(null);
  const [review, setReview] = useState<number | null>(null);

  // a link to one order opens it for review, once
  const opened = useRef(false);
  useEffect(() => {
    const want = params.get('q');
    if (opened.current || !data || !want) return;
    opened.current = true;
    const hit = data.orders.filter((o) => o.orderNumber === want);
    if (hit.length === 1) setReview(hit[0]!.id);
  }, [data, params]);

  const all = data?.orders ?? [];
  const count = (f: Filter) => all.filter(FILTERS[f]).length;
  const rows = useMemo(() => {
    let orders = data?.orders ?? [];
    if (filter) orders = orders.filter(FILTERS[filter]);
    const query = fold(q.trim());
    if (query) orders = orders.filter((o) => hasText(query, o.orderNumber, o.sapNumber, o.vesselName, o.vesselModel, o.customer));
    // what waits for Assembly first, then the latest
    return [...orders].sort((a, b) => Number(awaitsAcceptance(b)) - Number(awaitsAcceptance(a)) || b.lastModified.localeCompare(a.lastModified));
  }, [data, q, filter]);
  const toggle = (f: Filter) => setFilter((cur) => (cur === f ? null : f));

  const nAccept = count('accept');
  const nOpen = count('open');
  return (
    <>
      <PageHeader title="Assembly Orders">
        <SearchInput value={q} onChange={setQ} placeholder="Search SAP, vessel, customer" width={240} />
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Refreshing…' : 'Refresh'}</button>
      </PageHeader>

      {data && all.length > 0 && (
        <FactSentence>
          {nAccept
            ? <Fact tone="warn" onClick={() => toggle('accept')} pressed={filter === 'accept'}>{plural(nAccept, 'order')} to accept</Fact>
            : <Fact tone="ok">Nothing to accept</Fact>}
          <FactEnd>,</FactEnd>{' '}
          <Fact onClick={() => toggle('accepted')} pressed={filter === 'accepted'}>{count('accepted')} accepted</Fact>
          {' '}and{' '}
          <Fact tone="info" onClick={() => toggle('production')} pressed={filter === 'production'}>{count('production')} in production</Fact>
          {nOpen > 0 && <><FactEnd>;</FactEnd> <Fact tone="acc" onClick={() => toggle('open')} pressed={filter === 'open'}>{plural(nOpen, 'order')} open for Sales</Fact></>}
          <FactEnd>.</FactEnd>
        </FactSentence>
      )}

      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && rows.length === 0 && (
        <EmptyState
          title={all.length ? 'No matching orders' : 'No confirmed orders yet'}
          hint={all.length ? 'Clear the search or the filter.' : 'Orders Sales confirm in the configurator land here to accept.'}
        />
      )}

      {data && rows.length > 0 && (
        <div className="tbl-wrap">
          {/* phones: a card per order (vessel and stage, then "SAP · model · Rev · reqs · updated"); a tap reviews it */}
          <table className="tbl tbl-cards">
            <thead>
              <tr><th>Vessel</th><th>SAP</th><th>Model</th><th>Customer</th><th>Stage</th><th>Rev</th><th>Requirements</th><th>Updated</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const toAccept = awaitsAcceptance(o);
                return (
                  <tr
                    key={o.id}
                    className={`${toAccept ? 'ao-due' : ''}${isPhone ? ' tc-tap' : ''}`}
                    onClick={isPhone ? (e) => { if (!(e.target as HTMLElement).closest('button, a')) setReview(o.id); } : undefined}
                  >
                    <td className="tc-title">{o.vesselName || o.customer || o.orderNumber}</td>
                    <td className="tc-hide">{o.sapNumber || <span className="muted">No SAP</span>}</td>
                    <td className="tc-hide">{o.vesselModel || '-'}</td>
                    <td className="tc-hide">{o.customer || '-'}</td>
                    <td className="tc-meta ao-meta">
                      {[o.sapNumber || 'No SAP', o.vesselModel, `Rev ${o.revisionNumber}`, o.requirementCount ? `${o.openRequirementCount} of ${o.requirementCount} reqs open` : '', fmtDate(o.lastModified)].filter(Boolean).join(' · ')}
                    </td>
                    <td className="tc-end">
                      <StagePill order={o} side="assembly" />
                      {o.salesOpen && <span className="ao-open-tag" title="Open for Sales"><IconUnlock size={13} /></span>}
                    </td>
                    <td className="tc-hide">
                      {o.revisionNumber}
                      {o.acceptedRevision != null && o.acceptedRevision !== o.revisionNumber && <span className="muted"> (accepted {o.acceptedRevision})</span>}
                    </td>
                    <td className="tc-hide">{o.requirementCount ? `${o.openRequirementCount} of ${o.requirementCount} open` : <span className="muted">None</span>}</td>
                    <td className="muted tc-hide">{fmtDate(o.lastModified)}</td>
                    <td className="tc-actions ao-acts">
                      <button className={`btn btn-sm${toAccept ? ' btn-primary' : ''}`} onClick={() => setReview(o.id)}>{toAccept ? 'Review' : 'Open'}</button>
                      {can('sales:read') && (
                        <button className="btn btn-sm btn-ghost" title="Open this order in Sales Orders" onClick={() => navigate(`/sales/orders?q=${encodeURIComponent(o.orderNumber)}`)}>Sales →</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {review != null && <ReviewModal id={review} specFields={data?.specFields ?? []} categoryOrder={data?.categoryOrder ?? []} onClose={() => setReview(null)} />}
    </>
  );
}
